import React from 'react';
import { MessageSquare, Send, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { feedbackService } from '../../services/feedbackService';
import { APP_ROUTES } from '../../routes/routingContract';
import './FeedbackButton.css';
import InlineAlert from '../common/InlineAlert';
import { getUserFacingError } from '../../lib/userFacingErrors';

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'bug', label: 'Bug' },
  { value: 'idea', label: 'Idea' },
  { value: 'billing', label: 'Billing' },
  { value: 'advice_quality', label: 'Advice quality' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export default function FeedbackButton({ open: controlledOpen, onOpenChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [category, setCategory] = React.useState('general');
  const [severity, setSeverity] = React.useState('low');
  const [status, setStatus] = React.useState('idle');
  const [error, setError] = React.useState('');
  const textareaRef = React.useRef(null);
  const open = controlledOpen ?? uncontrolledOpen;

  const setOpen = React.useCallback((nextOpen) => {
    if (onOpenChange) {
      onOpenChange(nextOpen);
      return;
    }

    setUncontrolledOpen(nextOpen);
  }, [onOpenChange]);

  React.useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.setTimeout(() => textareaRef.current?.focus(), 0);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen]);

  const resetForm = React.useCallback(() => {
    setMessage('');
    setCategory('general');
    setSeverity('low');
    setError('');
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedMessage = message.trim();

    if (trimmedMessage.length < 10) {
      setError('Please add a little more detail.');
      return;
    }

    setStatus('submitting');
    setError('');

    try {
      const viewport = window.visualViewport;
      const payload = {
        message: trimmedMessage,
        category,
        severity,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        viewport_width: Math.round(viewport?.width || window.innerWidth || 0),
        viewport_height: Math.round(viewport?.height || window.innerHeight || 0),
        metadata: {
          path: window.location.pathname,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      const response = await feedbackService.createFeedback(payload);
      if (!response?.ok) {
        throw new Error(response?.error || 'Unable to send feedback');
      }

      setStatus('sent');
      resetForm();
      window.setTimeout(() => {
        setOpen(false);
        setStatus('idle');
      }, 1200);
    } catch (err) {
      setStatus('idle');
      setError(getUserFacingError(err, 'feedback'));
    }
  };

  const closeModal = () => {
    setOpen(false);
    setStatus('idle');
    setError('');
  };

  const openFeedback = () => {
    if (onOpenChange) {
      navigate(APP_ROUTES.feedback, {
        state: { backgroundPath: location.pathname },
      });
      return;
    }

    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className="feedback-trigger"
        onClick={openFeedback}
        aria-label="Share feedback"
        title="Share feedback"
      >
        <MessageSquare size={18} aria-hidden="true" />
        <span>Feedback</span>
      </button>

      {open ? (
        <div className="feedback-backdrop" role="presentation" onMouseDown={closeModal}>
          <section
            className="feedback-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="feedback-modal__header">
              <div>
                <h2 id="feedback-title">Share feedback</h2>
                <p>Tell us what happened or what would make Wealthy better.</p>
              </div>
              <button
                type="button"
                className="feedback-close"
                onClick={closeModal}
                aria-label="Close feedback form"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </header>

            <form className="feedback-form" onSubmit={handleSubmit}>
              <label className="feedback-field feedback-field--full">
                <span>Feedback</span>
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Describe the issue, idea, or anything that felt confusing."
                  rows={6}
                  maxLength={4000}
                  disabled={status === 'submitting'}
                />
              </label>

              <div className="feedback-form__row">
                <label className="feedback-field">
                  <span>Type</span>
                  <select
                    value={category}
                    onChange={(event) => setCategory(event.target.value)}
                    disabled={status === 'submitting'}
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>

                <label className="feedback-field">
                  <span>Severity</span>
                  <select
                    value={severity}
                    onChange={(event) => setSeverity(event.target.value)}
                    disabled={status === 'submitting'}
                  >
                    {SEVERITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              {error ? (
                typeof error === 'string'
                  ? <div className="feedback-error" role="alert">{error}</div>
                  : <InlineAlert compact title={error.title} message={error.message} />
              ) : null}
              {status === 'sent' ? (
                <div className="feedback-success" role="status">Feedback sent.</div>
              ) : null}

              <footer className="feedback-form__actions">
                <button type="button" className="feedback-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="feedback-primary"
                  disabled={status === 'submitting' || message.trim().length < 10}
                >
                  <Send size={16} aria-hidden="true" />
                  <span>{status === 'submitting' ? 'Sending' : 'Send'}</span>
                </button>
              </footer>
            </form>
          </section>
        </div>
      ) : null}
    </>
  );
}
